<template>
  <section id="trackingRequest" class="page-content">
      <header id="section-header">
          <p class="title has-text-grey">Tracking request</p>
          <p class="subtitle has-text-grey">Check container shipments status.</p>
      </header>
      <main id="section-body">
        <div class="columns">
          <div class="column">
            <div class="field">
              <label class="label">Code Type</label>
              <span v-if="codeTypes[trackingRequest.code_type]">{{ codeTypes[trackingRequest.code_type].name }}</span>
            </div>
            <div class="field">
              <label class="label">Code Value</label>
              {{ trackingRequest.code_value }}
            </div>
            <div class="field">
              <label class="label">Client Code</label>
              <span id="editable" v-if="!editing" @click="editing=true;">
                <span>{{ trackingRequest.client_code }}</span>
                <i class="fa fa-edit fa-lg"/>
              </span>
              <span class="editing" v-else>
                <input class="client-code" ref="client_code" type="text" v-model="client_code" @keypress.enter="update('client_code')"/>
                <span class="fa fa-check fa-lg" @click="update('client_code')"/>
                <span class="fa fa-close fa-lg" @click="editing=false"/>
              </span>
            </div>

          </div>
          <div class="column">
            <div class="field">
              <label class="label">Carrier</label>
              <span v-if="trackingRequest.carrier">{{ trackingRequest.carrier.official_name }}</span>
            </div>
            <div class="field">
              <label class="label">Status</label>
              {{ STATUS[trackingRequest.status] }}
            </div>
            <div class="field">
              <label class="label">Project</label>
              <span id="editable" v-if="!editingProject" @click="editingProject=true">
                <span v-if="trackingRequest.project && trackingRequest.project.id">{{ trackingRequest.project.id }} - {{ trackingRequest.project.name }}</span>
                <i class="fa fa-edit fa-lg"/>
              </span>
              <span class="editing" v-else>
                <div class="client-code">
                  <div class="select">
                    <select placeholder="Choose Project" name="project" id="project" v-model="project" @change="update('project')">
                      <option selected :value="null"> ---------- </option>
                      <option v-for="project in projects" :key="project.id" :value="project">{{ project.id }} - {{ project.name }}</option>
                    </select>
                  </div>
                  <span class="fa fa-close fa-lg" @click="editingProject=false"/>
                </div>
              </span>
            </div>
          </div>
          <div class="column">
             <div class="field">
              <label class="label">Created</label>
              {{ trackingRequest.created_at | dateToString }}
            </div>
            <div class="field">
              <label class="label">Contractual ETA at POD</label>
              <span id="editable" v-if="!editingEtaPod" @click="editingEtaPod=true">
                <span v-if="trackingRequest.contractual_eta_pod">{{ trackingRequest.contractual_eta_pod | dateToString }}</span>
                <i class="fa fa-edit fa-lg"/>
              </span>
              <div class="field half-page" v-else>
                <datepicker
                  :inputClass="'input'"
                  :format="'yyyy-MM-dd'"
                  :placeholder="'YYYY-MM-DD'"
                  :monday-first=true
                  :typeable=true
                  :clear-button=true
                  :clear-button-icon="'fa fa-times'"
                  v-model="contractual_eta_pod"
                  />
                <span class="fa fa-check fa-lg btn-act" @click="update('contractual_eta_pod'); editingEtaPod=false"/>
                <span class="fa fa-close fa-lg btn-act" @click="editingEtaPod=false"/>
              </div>
            </div>
            <div class="field">
              <label class="label">Contractual ETA at DLV</label>
              <span id="editable" v-if="!editingEtaDlv" @click="editingEtaDlv=true">
                <span v-if="trackingRequest.contractual_eta_dlv">{{ trackingRequest.contractual_eta_dlv | dateToString }}</span>
                <i class="fa fa-edit fa-lg"/>
              </span>
              <div class="field half-page" v-else>
                <datepicker
                  :inputClass="'input'"
                  :format="'yyyy-MM-dd'"
                  :placeholder="'YYYY-MM-DD'"
                  :monday-first=true
                  :typeable=true
                  :clear-button=true
                  :clear-button-icon="'fa fa-times'"
                  v-model="contractual_eta_dlv"
                  />
                <span class="fa fa-check fa-lg btn-act" @click="update('contractual_eta_dlv');editingEtaDlv=false"/>
                <span class="fa fa-close fa-lg btn-act" @click="editingEtaDlv=false"/>
              </div>
            </div>

          </div>
        </div>
        <div class="buttons">
          <div class="buttons-right">
            <button class="button is-primary is-small" @click="shipments_exclude_hidden=!shipments_exclude_hidden; reloadShipments()">
              <span>{{ shipments_exclude_hidden ?  'Show All' : 'Exclude Hidden' }}</span>
            </button>
          </div>
        </div>
        <data-table
          v-if="shipments"
          url="shipment"
          :objects="shipments"
          :schema="shipment_schema"
          :totalItems="shipments_count"
          :fields="tableHeaders"
          :ordering="orderingColumns"
          :alias="{
            'status_verbose': 'status',
            'lifecycle_status_verbose': 'lifecycle',
            'container_type_str': 'Container Type',
            'last_alert_code': 'Last Event',
            'client_code': 'Client Code',
            'carrier_name': 'Carrier',
            'bl_or_booking_number': 'BL / Booking',
            'pol_loc': 'POL',
            'pod_loc': 'POD',
            'etd': 'ETD',
            'atd': 'ATD',
            'dtd': 'DTD',
            'eta': 'ETA',
            'ata': 'ATA',
            'dta': 'DTA',
            'storage_days_at_pol': 'Storage days at POL',
            'storage_days_at_pod': 'Storage days at POD',
            'discrepancy_eta_at_pod': 'DTAcP',
            'discrepancy_eta_at_dlv': 'DTAcD',
            'days_in_tsp': 'DTSP',
            'days_in_tsp_planned': 'DTSP Planned',
            'days_in_pod_planned': 'DPOD Planned',
            'days_in_pol_planned': 'DPOL Planned',
            'created_at': 'Created',
            'sf_data': 'C. Form',
          }"
          title="Shipments"
        >
          <div slot-scope="actions">
            <div slot="trigger" class="button is-success is-small" v-if="!actions.object.hide" @click="hideShipment(actions.object)">
              <span>Hide</span>&nbsp;&nbsp;<i class="fa fa-lg fa-eye-slash"/>
            </div>
            <div slot="trigger" class="button is-success is-small" v-if="actions.object.hide" @click="showShipment(actions.object)">
              <span>Show</span>&nbsp;&nbsp;<i class="fa fa-lg fa-eye"/>
            </div>
          </div>
        </data-table>
        <div class="buttons">
          <router-link :to="{ name: 'tracking-request-list'}" class="button">Back</router-link>
          <div class="buttons-right">
            <modal-confirm
              :disabled="!trackingRequest.archivable"
              @confirm="retryTracking(trackingRequest)">
                <div slot="title">Confirm Retry</div>
                <span slot="body">Are you sure you want to <strong>retry</strong> this tracking?</span>
                <div slot="footer">
                  <button class="button" @click="showModal = false; $emit('close')">
                    No, I'm not
                  </button>
                  <button class="button is-primary" @click="showModal = false; $emit('confirm')">
                    Yes, I'm sure
                  </button>
                </div>
                <div slot="trigger" class="button is-success" v-if="['invalid'].indexOf(trackingRequest.status) >= 0">Retry</div>
              </modal-confirm>

              <modal-confirm
              :disabled="!trackingRequest.archivable"
              @confirm="changeStatus(trackingRequest, 'pending_archiving')">
                <div slot="title">Confirm archive</div>
                <span slot="body">Are you sure you want to <strong>archive</strong> this tracking?</span>
                <div slot="footer">
                  <button class="button" @click="showModal = false; $emit('close')">
                    No, I'm not
                  </button>
                  <button class="button is-primary" @click="showModal = false; $emit('confirm')">
                    Yes, I'm sure
                  </button>
                </div>
                <div slot="trigger" class="button is-success" v-if="['invalid', 'cancelled', 'archived', 'pending_archiving', 'pending_cancelling'].indexOf(trackingRequest.status) < 0">Archive</div>
              </modal-confirm>
              <modal-confirm
              :disabled="!trackingRequest.cancellable"
              @confirm="changeStatus(trackingRequest, 'pending_cancelling')">
                <div slot="title">Confirm cancel</div>
                <span slot="body">Are you sure you want to <strong>cancel</strong> this tracking?</span>
                <div slot="footer">
                  <button class="button" @click="showModal = false; $emit('close')">
                    No, I'm not
                  </button>
                  <button class="button is-primary" @click="showModal = false; $emit('confirm')">
                    Yes, I'm sure
                  </button>
                </div>
                <div slot="trigger" class="button is-success" v-if="['invalid', 'cancelled', 'archived', 'pending_archiving', 'pending_cancelling'].indexOf(trackingRequest.status) < 0">Cancel</div>
              </modal-confirm>
            </div>
        </div>
      </main>
  </section>
</template>

<script>
import {mapState, mapActions} from 'vuex'
import DataTable from '@/components/DataTable'
import TrackingRequest from '@/models/trackingRequest'
import Shipment from '@/models/shipment'
import moment from 'moment'
import ModalConfirm from '../components/ModalConfirm.vue'
import Datepicker from 'vuejs-datepicker'

export default {
  name: 'trackingRequest',
  components: {DataTable, ModalConfirm, Datepicker},
  created () {
    this.$emit('loading')
    const promises = [
      this.getCarriers().then(response => { this.carriers = response }),
      this.getCodeTypes().then(response => { this.codeTypes = response }),
      this.getProjects().then(response => { this.projects = response }),
      this.getTrackingRequest(this.$route.params.id).then(response => { this.trackingRequest = response }),
      this.getShipments(
        {
          id: this.$route.params.id,
          params: {
            exclude_hidden: this.shipments_exclude_hidden
          }
        }
      ).then(response => {
        this.shipments = response.results
        this.shipments_count = response.count
      })
    ]
    Promise.all(promises).then(data => { this.$emit('loaded') })
  },
  data () {
    return {
      STATUS: TrackingRequest.STATUS,
      orderingColumns: ['id', 'status_verbose', 'lifecycle_status_verbose', 'health', 'last_alert_code', 'container_number', 'bl_or_booking_number', 'client_code', 'carrier_name', 'pol_loc', 'pod_loc', 'etd', 'atd', 'dtd', 'eta', 'ata', 'dta', 'discrepancy_eta_at_pod', 'discrepancy_eta_at_dlv', 'storage_days_at_pol', 'storage_days_at_pod', 'last_update', 'created_at'],
      tableHeaders: [
        'id',
        'status_verbose',
        'lifecycle_status_verbose',
        'health',
        'last_alert_code',
        'container_number',
        'bl_or_booking_number',
        'pol_loc',
        'pod_loc',
        'etd',
        'atd',
        'dtd',
        'eta',
        'ata',
        'dta',
        'last_update',
        'created_at'
      ],
      SHIPMENTS_HEALTH: TrackingRequest.SHIPMENTS_HEALTH,
      trackingRequest: {},
      codeTypes: [],
      carriers: [],
      client_code: '',
      editing: false,
      editingProject: false,
      editingEtaDlv: false,
      editingEtaPod: false,
      contractual_eta_dlv: '',
      contractual_eta_pod: '',
      projects: [],
      project: {},
      shipments: [],
      shipments_count: 0,
      shipments_exclude_hidden: true,
      shipment_schema: Shipment.schema()
    }
  },
  watch: {
    editing (value) {
      if (value) {
        this.client_code = this.trackingRequest.client_code
        this.$nextTick(() => this.$refs.client_code.focus())
      }
    }
  },
  methods: {
    ...mapActions({
      getTrackingRequest: 'trackingRequest/retrieve',
      getProjects: 'project/list',
      getCarriers: 'carrier/list',
      getCodeTypes: 'codeType/list',
      getShipments: 'trackingRequest/shipments',
      saveTrackingRequest: 'trackingRequest/save',
      updateTrackingRequest: 'trackingRequest/patch',
      postHideShipment: 'shipment/hide',
      postShowShipment: 'shipment/show'
    }),
    update (property) {
      this.trackingRequest[property] = this[property]
      var upload = {}
      upload[property] = (this[property] && this[property].id ? this[property].id : this[property])
      upload.id = this.trackingRequest.id
      this.updateTrackingRequest(upload)
      .then(response => {
        this.editing = false
      })
    },
    save () {
      this.saveTrackingRequest(this.trackingRequest).then(response => {
        this.$router.push(`/tracking-requests/${this.trackingRequest.id}`)
      })
    },
    reloadShipments () {
      this.getShipments(
        {
          id: this.$route.params.id,
          params: {
            exclude_hidden: this.shipments_exclude_hidden
          }
        }
      ).then(response => {
        this.shipments = response.results
        this.shipments_count = response.count
      })
    },
    hideShipment (object) {
      this.postHideShipment(object.id).then(response => {
        this.$notify({
          group: 'global',
          title: 'Success',
          text: 'The Shipment has been hidden',
          duration: 4000,
          type: 'success'
        })
        this.reloadShipments()
      })
    },
    showShipment (object) {
      this.postShowShipment(object.id).then(response => {
        this.$notify({
          group: 'global',
          title: 'Success',
          text: 'The Shipment has been showed',
          duration: 4000,
          type: 'success'
        })
        this.reloadShipments()
      })
    }
  },
  computed: {
    readonly () {
      return this.trackingRequest.status !== 'new'
    },
    ...mapState({
      shipmentsSchema: 'shipment/schema'
    })
  },
  filters: {
    dateToString: function (value) {
      if (value instanceof Date && !isNaN(value.valueOf())) {
        let dateTimeString = moment(value).format('YYYY-MM-DD HH:mm')
        return dateTimeString
      }
      return ''
    }
  }
}
</script>

<style lang="scss">
  .data-table.card{
    margin-top: 30px;
  }

#editable {
  display:inline-block;
  transition:.2s;
  i{
    transition:.2s;
    cursor: pointer;
    margin-top: 3px;
    margin-left: 4px;
  }
}
.buttons {
  display: flex;
  .buttons-right {
    flex: 1;
    display: inline-flex;
    justify-content: flex-end;
  }
  .button {
    margin: .1em;
  }
}
.half-page{
    display: flex;
    .btn-act {
      text-align: center;
      height: 100%;
      width: 25px;
      border: solid 1px #BBBBBB;
      display: inline-block;
      margin-top: 3px;
      padding: 3px;
      min-height: 23px;
    }
    .fa-check:hover{
        color:green;
      }
      .fa-close:hover{
        color:darkred;
      }
  }
</style>



// WEBPACK FOOTER //
// src/pages/PageTrackingRequest.vue